import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, Input } from '@atlas/ui';

import PaymentMethodItem from 'components/ClientProfile/PaymentTab/PaymentMethodItem';

import { submitWithdrawal } from '../withdrawalUtils';

import { InfoLabel } from './InfoLabel';

export const WithdrawalAlfacoinPopup = ({
  data: { addressKey = 'cryptoAddress', paymentMethod, redirectUrl, withTag, withoutInfo },
  id,
}) => {
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);
  const [isActiveAddressInput, setActiveAddressInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [address, setAddress] = useState();
  const [tag, setTag] = useState();
  const {
    amount,
    currencyFrom,
    currencyTo,
    exchangeAmount,
    exchangeRate,
    extTxId,
    paymentId,
    pmName,
  } = parseWithdrawalQuery(redirectUrl) || {};
  const { id: pmId } = paymentMethod;

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleChangeInput = useCallback(data => setAddress(data.target.value.trim()), []);
  const handleChangeTagInput = useCallback(data => setTag(data.target.value.trim()), []);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const additionalParameters = {
        [addressKey]: address,
        pmName,
        publicPaymentId: extTxId || paymentId,
      };

      if (withTag) {
        additionalParameters.cryptoTag = tag;
      }

      const requestData = { additionalParameters, amount, currency: currencyFrom, pmId };
      await submitWithdrawal({ amount, currency: currencyFrom, paymentMethod, requestData });
    } catch {
      NotificationManager.notify({
        message: 'withdrawal_error_message',
        status: 'error',
        type: 'notification',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    addressKey,
    address,
    pmName,
    extTxId,
    paymentId,
    withTag,
    amount,
    currencyFrom,
    pmId,
    tag,
    paymentMethod,
    handleClose,
  ]);

  const currencyToFormatter = useMemo(
    () => new CryptoFormatter(languageCode, currencyTo),
    [currencyTo, languageCode],
  );

  return (
    <DefaultPopup headerTitle="withdrawal" headerTitlePosition="left" onClose={handleClose}>
      <div className="withdrawal-bitcoin__body">
        <div className="withdrawal-bitcoin__payment-method">
          <PaymentMethodItem paymentMethod={paymentMethod} />
        </div>
        <div
          className={cx('withdrawal-bitcoin__from-wrapper', {
            'with-tag': withTag,
            'without-info': withoutInfo,
          })}
        >
          <div className="withdrawal-bitcoin__addresses">
            <Input
              active={isActiveAddressInput === 'address'}
              className="withdrawal-bitcoin__input"
              label="address"
              onBlur={() => setActiveAddressInput('')}
              onChange={handleChangeInput}
              onFocus={() => setActiveAddressInput('address')}
              value={address}
            />
          </div>

          {withTag && (
            <div className="withdrawal-bitcoin__tag">
              <Input
                className="withdrawal-bitcoin__input"
                disabled={isSubmitting}
                label="crypto_tag"
                onChange={handleChangeTagInput}
                value={tag}
              />
            </div>
          )}
        </div>

        {!withoutInfo && (
          <div className="withdrawal-bitcoin__info">
            <div>
              <InfoLabel label="withdrawal_rate" />
              {`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}
            </div>

            <div>
              <InfoLabel label="amount" />
              {`${IntlManager.formatMoney(amount)} (≈ ${currencyToFormatter.format(exchangeAmount * 10 ** 9)})`}
            </div>

            <div className="withdrawal-bitcoin__total">
              <InfoLabel label="total_amount" />
              {`≈ ${currencyToFormatter.format(exchangeAmount * 10 ** 9)}`}
            </div>
          </div>
        )}

        <Button
          className="withdrawal-bitcoin__button"
          disabled={!address || isSubmitting}
          label="withdrawal"
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </DefaultPopup>
  );
};

WithdrawalAlfacoinPopup.propTypes = {
  data: PropTypes.shape({
    addressKey: PropTypes.string,
    paymentId: PropTypes.string,
    redirectUrl: PropTypes.string,
    withTag: PropTypes.bool,
    withoutInfo: PropTypes.bool,
  }).isRequired,
  id: PropTypes.string.isRequired,
};
